import React, { useEffect, useRef, useState, useCallback, FC, useMemo } from "react";
import { ActionCreatorsMapObject } from "redux";
import { withRouter, RouteComponentProps } from "react-router-dom";
import { ExclamationCircleFilled } from "src/icons";
import { IAction } from "src/store";
import Shimmer from "src/shared/components/CustomisedShimmer";
import { safeGet } from "src/shared/utils";
import styles from "./index.module.scss";
import AreaChartMini from "src/shared/components/AreaChart";
import { ISinglePin } from "src/shared/types";
import { PencilIcon } from "src/assets/pencil-icon.js";
import ChartFallback from "src/shared/components/ChartFallback";
import { getTimeRoundOffData } from "src/shared/utils/timeUtils";
import { GRANULARITY_MAPPING } from "src/shared/config";
import { IsTabHidden } from "src/shared/utils/customTab";

interface IDispatchProps extends ActionCreatorsMapObject {
  fetchChart: (sliceId: number) => IAction<string | symbol, any>;
}
interface IOverviewCardProps {
  data: ISinglePin;
  showDetails(id: any, name: string): void;
  id: number;
  failure: boolean;
  pending: boolean;
  selectedTab: string;
}

export type WDYR = {
  whyDidYouRender?: boolean;
};

const flattenFilters = (filterObj) => {
  const reducer = (accumulator, currentValue) => {
    const a = accumulator.concat(...currentValue.value);
    return a;
  };
  return filterObj.reduce(reducer, []).join(" | ");
};

const OverviewCard: FC<IOverviewCardProps & RouteComponentProps> & WDYR = (props) => {
  const navigateToTakeNotes = useCallback(
    (evt) => {
      evt.stopPropagation();
      props.history.push(`${props.id}/takeNotes`);
    },
    [props.id]
  );
  const isChartFetched = typeof safeGet(props, "data.currentChart") === "number" ? false : true;

  const cardRef = useRef<HTMLDivElement>(null);
  const thisChartData = safeGet(props, "data.trendChart.data.data[0].values", []);
  const { failure, pending } = props;
  const { bigNumber, vCompare, progress, isPositive, isHidden } = safeGet(props, "data", {});
  const [cardWidth, setCardWidth] = useState(0);
  const [cardHeight, setCardHeight] = useState(0);
  const clickCard = useCallback(
    (ev: React.MouseEvent) => {
      if (ev) {
        const metricId = ev.currentTarget.getAttribute("data-item");
        props.showDetails(metricId, safeGet(props, "data.label", ""));
      }
    },
    [props.showDetails]
  );

  const barsConfig = useMemo(
    () => [
      { height: "1.872rem", width: "5.52rem", repeatBar: 1 },
      {
        height: "1.24rem",
        width: "4.16rem",
        repeatBar: 1,
        style: { marginTop: "6%" },
      },
    ],
    []
  );

  const dataPoints = useCallback(
    () => getTimeRoundOffData(thisChartData, GRANULARITY_MAPPING[props.selectedTab]),
    [thisChartData]
  )();

  useEffect(() => {
    setCardWidth(safeGet(cardRef, "current.clientWidth", 0));
    setCardHeight(safeGet(cardRef, "current.clientHeight", 0));
  }, []);

  return (
    <main className={styles["overview-card"]} onClick={clickCard} data-item={safeGet(props, "data.id")}>
      <div className={styles["ovcard-grid-container"]} ref={cardRef}>
        <div className={styles["ovcard-item1"]}>
          <div className={styles["ovcard-item1-head"]}>{props.data.label && props.data.label.toUpperCase()}</div>
          <div className={styles["ovcard-item1-filter"]}>{flattenFilters(props.data.filters)}</div>
        </div>

        <div className={styles["ovcard-item2"]}>
          {!pending && !failure && isChartFetched && !IsTabHidden(props.data.isHidden, props.selectedTab) ? (
            <p>
              Updated at <span className={styles["last-updated"]}>{(props as any).data.lastUpdatedTimeStamp}</span>
            </p>
          ) : failure || IsTabHidden(props.data.isHidden, props.selectedTab) ? null : (
            <div>
              <Shimmer
                barsConfig={[{ height: "1.872rem", width: "10rem", repeatBar: 1 }]}
                className={styles["Shimmer"]}
              />
            </div>
          )}
        </div>
        <div className={styles["ovcard-item3"]}>
          {safeGet(props, "data.notes", []).length > 0 && (
            <>
              <ul className={styles["summary-data"]}>
                {safeGet(props, "data.notes", []).map((item, index) => (
                  <li key={`${item.updatedAt}-${item.text}-${index}`}>{item.text}</li>
                ))}
              </ul>
              <div className={styles["edit-notes"]}>
                <PencilIcon onClick={navigateToTakeNotes} />
              </div>
            </>
          )}
          {safeGet(props, "data.notes", []).length === 0 && (
            <>
              <div className={styles["add-notes"]} onClick={navigateToTakeNotes}>
                + Add notes
              </div>
            </>
          )}
        </div>
        {!pending && (isChartFetched || failure) ? (
          isChartFetched ? (
            <div className={styles["ovcard-item4"]}>
              <div className={styles["chart-number"]}>
                {(safeGet(props, "data.isAnomalous", false) || safeGet(props, "data.isStaleMetric", false)) && (
                  <ExclamationCircleFilled className={styles["anomaly"]} />
                )}
                <span>{!IsTabHidden(props.data.isHidden, props.selectedTab) ? bigNumber : "N/A"}</span>
              </div>
              <div
                className={
                  vCompare === ""
                    ? styles["chart-progress-neutral"]
                    : `${progress == isPositive ? styles["chart-progress-up"] : styles["chart-progress-down"]} ${
                        progress ? styles["up-arrow"] : styles["down-arrow"]
                      }`
                }
              >
                {!IsTabHidden(props.data.isHidden, props.selectedTab) ? vCompare : ""}
              </div>
              <div className={styles["pinned-chart"]}>
                {!IsTabHidden(props.data.isHidden, props.selectedTab) ? (
                  <AreaChartMini
                    data={dataPoints}
                    chartProgress={isPositive ? progress : !progress}
                    width={cardWidth / 4.5}
                    height={cardHeight / 5}
                    isNeutral={vCompare === ""}
                  />
                ) : (
                  ""
                )}
              </div>
            </div>
          ) : (
            <div className={styles["ovcard-item4-404"]}>
              <ChartFallback />
            </div>
          )
        ) : (
          <div className={styles["ovcard-item4-loader"]}>
            <Shimmer barsConfig={barsConfig} className={styles["Shimmer"]} />
          </div>
        )}
      </div>
    </main>
  );
};
OverviewCard.whyDidYouRender = false;

export default React.memo(withRouter(OverviewCard));

